import React from 'react';
import { DiffChange } from 'src/services';
import { Link } from 'src/components-dummy';
import { EntityDiffSchema } from '../../types';
import { ImageLink } from '../custom-inspirations-galleries/image-url';

const arrayIndexPattern = '\\d+';

function getIndexedDisplayName(baseName: string, diff: DiffChange): string {
  const match = diff.valuePath.match(/\.(\d+)(?:\.[^.]+)?$/);
  return match ? `${baseName} ${Number(match[1]) + 1}` : baseName;
}

interface InspirationGallerySkuImage {
  imageUrl?: string;
  sku?: string;
  altText?: string;
}

function getSkuImage(value: unknown): InspirationGallerySkuImage | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as InspirationGallerySkuImage;
    } catch {
      return { imageUrl: value };
    }
  }

  return value as InspirationGallerySkuImage;
}

function renderSkuImage(value: unknown): JSX.Element | string {
  const skuImage = getSkuImage(value);

  if (!skuImage?.imageUrl) {
    return 'N/A';
  }

  return (
    <>
      <ImageLink imageUrl={skuImage.imageUrl} />
      {skuImage.sku && ` · SKU: ${skuImage.sku}`}
      {skuImage.altText && ` · Alt Text: ${skuImage.altText}`}
    </>
  );
}

export function getLocaleUrlMap(value: unknown): Record<string, string> | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as Record<string, string>;
    } catch {
      return undefined;
    }
  }

  return value as Record<string, string>;
}

function renderLocaleUrlMap(value: unknown): JSX.Element | string {
  const localeUrlMap = getLocaleUrlMap(value);
  const entries = localeUrlMap ? Object.entries(localeUrlMap).filter(([, url]) => !!url) : [];

  if (!entries.length) {
    return 'N/A';
  }

  return (
    <>
      {entries.map(([locale, url], index) => (
        <React.Fragment key={locale}>
          {index > 0 && <br />}
          {locale}: <Link href={url}>{url}</Link>
        </React.Fragment>
      ))}
    </>
  );
}

export const inspirationGalleryDiffSchema: EntityDiffSchema<
  Record<string, unknown>,
  Record<string, unknown>
> = {
  [`boundingBox.inspirationGalleryImages.${arrayIndexPattern}$`]: {
    displayName: 'Inspiration Gallery Image',
    getDisplayName: diff => getIndexedDisplayName('Inspiration Gallery Image', diff),
    getValue({ value }) {
      return typeof value === 'string' ? <ImageLink imageUrl={value} /> : 'N/A';
    },
  },
  [`boundingBox.inspirationGalleryImagesSkus.${arrayIndexPattern}$`]: {
    displayName: 'Inspiration Gallery SKU Image',
    getDisplayName: diff => getIndexedDisplayName('Inspiration Gallery SKU Image', diff),
    getValue({ value }) {
      return renderSkuImage(value);
    },
  },
  [`boundingBox.inspirationGalleryImagesSkus.${arrayIndexPattern}.imageUrl$`]: {
    displayName: 'Inspiration Gallery SKU Image',
    getDisplayName: diff => getIndexedDisplayName('Inspiration Gallery SKU Image', diff),
    getValue({ value }) {
      return typeof value === 'string' ? <ImageLink imageUrl={value} /> : 'N/A';
    },
  },
  [`boundingBox.inspirationGalleryImagesSkus.${arrayIndexPattern}.sku$`]: {
    displayName: 'Inspiration Gallery SKU',
    getDisplayName: diff => getIndexedDisplayName('Inspiration Gallery SKU', diff),
  },
  [`boundingBox.inspirationGalleryImagesSkus.${arrayIndexPattern}.altText$`]: {
    displayName: 'Inspiration Gallery SKU Image Alt Text',
    getDisplayName: diff => getIndexedDisplayName('Inspiration Gallery SKU Image Alt Text', diff),
  },
  showFilters: {
    displayName: 'Show Filters in Results Modal',
    relevantFieldPaths: ['enableMobileFilters', 'enableDesktopFilters'],
    getValue({ value }) {
      return value === true || value === 'true' ? 'Enabled' : 'Disabled';
    },
  },
  showSort: {
    displayName: 'Show Sort Menu in Results Modal',
    relevantFieldPaths: ['enableMobileSort', 'enableDesktopSort'],
    getValue({ value }) {
      return value === true || value === 'true' ? 'Enabled' : 'Disabled';
    },
  },
  enablePersonalisationTourImages: { displayName: 'Enable Personalisation Tour Images' },
  'mobileTourScreen.active$': { displayName: 'Mobile Tour Screen Active' },
  'mobileTourScreen.tourInfoLinkUrl$': {
    displayName: 'Mobile Tour Info Link URL',
    getValue({ value }) {
      return typeof value === 'string' && value ? <Link href={value}>{value}</Link> : 'N/A';
    },
  },
  'mobileTourScreen.tourInfoLinkUrlByLocale$': {
    displayName: 'Mobile Tour Info Link URLs (by locale)',
    getValue({ value }) {
      return renderLocaleUrlMap(value);
    },
  },
};
